import type {
	AvailableSpaceConfiguration,
	CapacityPage,
	Hub,
	HubMeta,
	HubPage,
	HubService,
	ReservationPage,
	SncOrderPage,
	SncProductPage,
	Space,
} from "../types";
import { HubStatus, HubService as HubServiceEnum, SpaceConfiguration, SpaceStatus } from "../types";
import { organizations } from "./organization";

const emptyReservationPage: ReservationPage = {
	nodes: [],
	totalCount: 0,
	totalPages: 0,
};

const emptyCapacityPage: CapacityPage = {
	nodes: [],
	totalCount: 0,
	totalPages: 0,
};

const emptySncOrdersPage: SncOrderPage = {
	nodes: [],
	totalCount: 0,
	totalPages: 0,
};

const emptySncProductsPage: SncProductPage = {
	nodes: [],
	totalCount: 0,
	totalPages: 0,
};

const createSpaces = (hubName: string): Space[] =>
	Array.from({ length: 10 }, (_, i) => ({
		id: `${hubName}-space-${i}`,
		configuration: SpaceConfiguration.BaceStorefrontS,
		deviceId: "virtual",
		dimensions: {
			depthMm: 500,
			heightMm: 105,
			widthMm: 550,
		},
		frame: `${i < 10 / 2 ? "A" : "B"}${(i % 2) + 1}`,
		lastUpdate: "2024-01-15T10:00:00Z",
		lock: i,
		position: i,
		reservations: emptyReservationPage,
		status: SpaceStatus.Closed,
	})) as Space[];

const SpaceConfigStorefrontS = (hubName: string): AvailableSpaceConfiguration => ({
	configuration: SpaceConfiguration.BaceStorefrontS,
	numSpaces: 10,
	dimensions: {
		depthMm: 500,
		heightMm: 105,
		widthMm: 550,
	},
	spaces: createSpaces(hubName),
});

const createMeta = (services: HubService[]): Omit<HubMeta, "hub"> => ({
	id: "hub-meta-alice",
	additionalInfo: "Beside the main entrance",
	b2CCapacityMin: 0,
	capacityOverview: {
		b2CCapacityMin: 0,
		b2CCapacityUtilized: 0,
		fixedCapacity: 0,
		flexCapacityAvailable: 10,
		flexCapacityUtilized: 0,
		sharedCapacity: 10,
		sharedCapacityAvailable: 10,
		totalSpaces: 10,
	},
	city: "Nuernberg",
	country: "Germany",
	lat: 49.4521,
	lng: 11.0767,
	name: "Alice",
	organization: organizations.Bace,
	organizationCapacity: {
		fixedAvailable: 10,
		fixedCapacity: 0,
		fixedUtilized: 0,
		flexUtilized: 0,
	},
	postalCode: "90478",
	serviceProviders: [organizations.DM, organizations.Rossmann],
	services,
	street: "Schlossstrasse 56",
});

const createHub = ({
	id,
	slug,
	status,
	metaId,
	metaName,
	services,
}: {
	id: string;
	slug: string;
	status: HubStatus;
	metaId: string;
	metaName: string;
	services: HubService[];
}): Hub => {
	const spaces = createSpaces(id);

	const hub = {
		id,
		updatedAt: "2024-01-15T10:00:00Z",
		version: "mock-1",
		slug,
		status,
		availableConfigurations: [SpaceConfigStorefrontS(id)],
		capacities: emptyCapacityPage,
		products: {
			nodes: [],
			totalCount: 0,
			totalPages: 0,
		},
		sncOrders: emptySncOrdersPage,
		sncProducts: emptySncProductsPage,
		spaceConfigurations: [SpaceConfiguration.BaceStorefrontS],
		spaces,
		meta: {
			...createMeta(services),
			id: metaId,
			name: metaName,
		},
	} as Hub;

	for (const space of spaces) {
		space.hub = hub;
	}

	if (hub.meta) {
		hub.meta.hub = hub;
	}

	return hub;
};

const DefaultHub = createHub({
	id: "ALICE",
	slug: "alice",
	status: HubStatus.Online,
	metaId: "hub-meta-alice",
	metaName: "Alice",
	services: [HubServiceEnum.InstantBuy, HubServiceEnum.Onedelivery],
});

const OfflineHub = createHub({
	id: "ALICE_OFFLINE",
	slug: "alice-offline",
	status: HubStatus.Offline,
	metaId: "hub-meta-alice-offline",
	metaName: "Alice Offline",
	services: [HubServiceEnum.InstantBuy, HubServiceEnum.Onedelivery],
});

export const hubs = {
	Default: DefaultHub,
	Offline: OfflineHub,
	page: {
		nodes: [DefaultHub, OfflineHub] as Hub[],
		totalCount: 2,
		totalPages: 1,
	} satisfies HubPage,
};
